import { Image } from 'expo-image';
import { Modal, StyleSheet, View } from 'react-native';

import { infoIcon } from '@/assets/images/icons';
import Text from '@/components/Text';
import { InfoModalTypes, useInfoModal } from '@/contexts/infoModalContext';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import ModalCard from './ModalCard';

const InfoModal = () => {
  const colors = useColor();
  const {
    state: {
      type,
      icon,
      message,
      title,
      subtitle,
      subtitle2,
      showDoneButton = true,
      buttonLabel,
      buttonColor,
      showClose,
      closeOnTapOutside,
      onButtonPress,
      visible,
      accessoryView
    },
    dispatch
  } = useInfoModal();

  const buttonText = buttonLabel ?? i18n.t('mix.close');

  const closeModal = () => {
    dispatch({ type: 'SET_VISIBLE', setVisible: false });
  };

  const style = useStyle(type);

  if (type === 'tooltip' && title && message) {
    return (
      <Modal animationType="fade" transparent={true} visible={visible} statusBarTranslucent>
        <ModalCard
          closeModal={closeModal}
          showCloseButton={showClose}
          modalStyle={style.modalStyle}
          closeOnTapOutside={closeOnTapOutside}>
          <View style={style.tooltipContainer}>
            <View style={style.tooltipTitleContainer}>
              <Text fontWeight="700" style={[style.tooltipTitle, { color: colors.titleText }]}>
                {title}
              </Text>
              <Image source={infoIcon} contentFit={'contain'} style={style.tooltipImage} />
            </View>
            <Text style={style.tooltipText}>{message}</Text>
          </View>
        </ModalCard>
      </Modal>
    );
  }

  return (
    <Modal animationType="fade" transparent={true} visible={visible} statusBarTranslucent>
      <ModalCard
        closeModal={closeModal}
        showCloseButton={showClose}
        modalStyle={style.modalStyle}
        closeOnTapOutside={closeOnTapOutside}>
        {icon && <Image source={icon} contentFit={'contain'} style={style.image} />}
        <Text fontWeight="700" style={[style.titleText, { color: colors.titleText }]}>
          {title}
        </Text>
        {subtitle ? <Text style={style.subtitle}>{subtitle}</Text> : <></>}
        {subtitle2 ? <Text style={style.subtitle2}>{subtitle2}</Text> : <></>}
        {message ? (
          <Text fontWeight="700" style={style.message}>
            {message}
          </Text>
        ) : (
          <></>
        )}
        {accessoryView !== undefined && accessoryView}
        {showDoneButton && (
          <BrandedButton
            style={style.button}
            onPress={
              onButtonPress
                ? () => {
                    onButtonPress();
                    closeModal();
                  }
                : closeModal
            }
            backGroundColor={buttonColor ?? '#00C1EB'}>
            {buttonText}
          </BrandedButton>
        )}
      </ModalCard>
    </Modal>
  );
};

const useStyle = (type: InfoModalTypes) => {
  const style = StyleSheet.create({
    modalStyle: {
      paddingHorizontal: 18,
      paddingBottom: 36,
      alignItems: 'center'
    },

    tooltipContainer: {
      justifyContent: 'center',
      alignItems: 'center'
    },

    tooltipTitleContainer: {
      flexDirection: 'row',
      gap: 5,
      justifyContent: 'center',
      alignItems: 'center'
    },

    tooltipTitle: {
      fontSize: 24,
      textAlign: 'center'
    },

    tooltipText: {
      paddingVertical: 10,
      fontSize: 16,
      textAlign: 'center'
    },

    tooltipImage: {
      height: 26,
      width: 26
    },

    image: {
      height: 70,
      width: 70,
      marginVertical: 5
    },

    titleText: {
      fontSize: type === 'title' ? 22 : 32,
      ...(type === 'title' && { marginVertical: 45 }),
      textAlign: 'center'
    },

    subtitle: {
      fontSize: 14,
      textAlign: 'center',
      marginVertical: 20
    },

    subtitle2: {
      fontSize: 12,
      textAlign: 'center'
    },

    message: {
      fontSize: 14,
      textAlign: 'center',
      marginVertical: 34
    },

    button: {
      width: '100%',
      height: 44
    }
  });

  return style;
};

export default InfoModal;
